import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, ExternalLink, Check, Link as LinkIcon, Building2, Mail, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CaseReferences — compact panel that shows every external identifier a
 * case carries: NamUs #, Doe Network ID, NCIC, agency case #, ME case #,
 * DNA Doe Project URL, original source URL, and a copy-link button.
 *
 * Only renders when at least one identifier field is populated.
 */
export default function CaseReferences({ caseData }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [agencyCopied, setAgencyCopied] = useState(false);
  const [drafting, setDrafting] = useState(false);
  if (!caseData) return null;

  const namus = caseData.namus_number || null;
  const doeId = caseData.doe_network_id || caseData.source_id || null;
  const ncic = caseData.ncic_number || null;
  const agencyCase = caseData.agency_case_number || caseData.case_number || null;
  const meCase = caseData.me_case_number || null;
  const ddpUrl = caseData.ddp_case_url || null;
  const sourceUrl = caseData.source_url || null;
  const flags = Array.isArray(caseData.flags) ? caseData.flags : [];

  const reportingAgency = caseData.reporting_agency || null;
  const reportingPhone = caseData.reporting_agency_phone || null;
  const reportingCaseNumber = caseData.reporting_agency_case_number || null;
  const reportingSource = caseData.reporting_agency_source || null;
  const reportingSourceUrl = caseData.reporting_agency_source_url || null;

  const hasDdpActive = flags.includes('DNA_DOE_PROJECT_ACTIVE');
  const hasDdpSolved = flags.includes('DNA_DOE_PROJECT_SOLVED');

  const hasAnyRef = namus || doeId || ncic || agencyCase || meCase || ddpUrl
                    || sourceUrl || reportingAgency;
  if (!hasAnyRef) return null;

  // Build NamUs public URL based on prefix
  let namusUrl = null;
  if (namus) {
    const up = String(namus).toUpperCase();
    const digits = up.replace(/^(UP|UCP|MP)/, '');
    if (up.startsWith('UP') || up.startsWith('UCP')) {
      namusUrl = `https://www.namus.gov/UnidentifiedPersons/Case#/${digits}`;
    } else if (up.startsWith('MP')) {
      namusUrl = `https://www.namus.gov/MissingPersons/Case#/${digits}`;
    } else if (/^\d+$/.test(up) && caseData.case_type === 'unidentified_person') {
      namusUrl = `https://www.namus.gov/UnidentifiedPersons/Case#/${up}`;
    } else if (/^\d+$/.test(up)) {
      namusUrl = `https://www.namus.gov/MissingPersons/Case#/${up}`;
    }
  }

  const doeUrl = doeId
    ? `http://www.doenetwork.org/cases/${String(doeId).toLowerCase()}.html`
    : null;

  const canonicalShareKey = namus || doeId || caseData.id;
  const shareUrl = `https://linkthemissing.com/case/${canonicalShareKey}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied', { description: shareUrl });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Unable to copy link');
    }
  };

  const copyAgencyBlock = async () => {
    if (!reportingAgency) return;
    const parts = [
      `Agency: ${reportingAgency}`,
      `Phone: ${reportingPhone || '—'}`,
      `Case #: ${reportingCaseNumber || '—'}`,
    ];
    const text = parts.join(' | ');
    try {
      await navigator.clipboard.writeText(text);
      setAgencyCopied(true);
      toast.success('Agency contact copied', { description: text });
      setTimeout(() => setAgencyCopied(false), 1500);
    } catch {
      toast.error('Unable to copy agency contact');
    }
  };

  const sourceLabel = reportingSource === 'ca_doj_oag'
    ? 'CA DOJ'
    : reportingSource === 'namus'
      ? 'NamUs'
      : reportingSource || '—';

  const draftOutreach = async () => {
    if (!reportingAgency) return;
    setDrafting(true);
    try {
      const r = await fetch(`${API}/admin/outreach/draft-to-agency`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: caseData.id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `${r.status}`);
      toast.success(
        `Draft queued — pending approval · channel: ${d.dispatch_channel}`,
        { duration: 5000 },
      );
      navigate(`/admin/outreach?open=${d.outreach_id}`);
    } catch (e) {
      toast.error(`Draft failed: ${e.message}`);
    } finally {
      setDrafting(false);
    }
  };

  const Row = ({ label, value, href, badge = null }) => (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="text-muted-foreground w-28 shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-trust-blue-700 dark:text-trust-blue-400 hover:underline truncate"
          >
            {value}
            <ExternalLink className="inline w-3 h-3 ml-1 opacity-60" />
          </a>
        ) : (
          <span className="font-mono text-slate-800 dark:text-slate-200 truncate">
            {value}
          </span>
        )}
        {badge}
      </div>
    </div>
  );

  return (
    <Card
      data-testid="case-references-panel"
      className="p-4 mb-4 bg-slate-50/80 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <LinkIcon className="w-4 h-4" />
          Case References
        </h3>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5"
          onClick={copyLink}
          data-testid="case-ref-copy-link-btn"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" /> Copy link
            </>
          )}
        </Button>
      </div>

      <div className="space-y-1.5">
        {namus && (
          <Row label="NamUs #" value={namus} href={namusUrl} />
        )}
        {doeId && (
          <Row label="Doe Network" value={doeId} href={doeUrl} />
        )}
        {ncic && <Row label="NCIC #" value={ncic} />}
        {meCase && <Row label="ME case #" value={meCase} />}
        {agencyCase && <Row label="Agency case #" value={agencyCase} />}
        {ddpUrl && (
          <Row
            label="DNA Doe Project"
            value={new URL(ddpUrl).pathname.replace(/^\/case\//, '').replace(/\/$/, '')}
            href={ddpUrl}
            badge={
              hasDdpActive ? (
                <Badge className="bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300 text-[10px]">
                  ACTIVE
                </Badge>
              ) : hasDdpSolved ? (
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 text-[10px]">
                  SOLVED
                </Badge>
              ) : null
            }
          />
        )}
      </div>

      {sourceUrl && (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-trust-blue-700 dark:text-trust-blue-400 hover:underline"
          data-testid="case-ref-source-link"
        >
          <ExternalLink className="w-3 h-3" />
          View original record
        </a>
      )}

      {reportingAgency && (
        <div
          data-testid="agency-contact-card"
          className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-300">
              <Building2 className="w-4 h-4" />
              Reporting Agency
              <Badge
                variant="outline"
                className="ml-1 text-[10px] border-slate-300 dark:border-slate-600"
                data-testid="agency-contact-card-source-badge"
              >
                {sourceLabel}
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5"
                onClick={copyAgencyBlock}
                data-testid="agency-contact-card-copy-btn"
              >
                {agencyCopied ? (
                  <>
                    <Check className="w-3.5 h-3.5" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" /> Copy for outreach letter
                  </>
                )}
              </Button>
              <Button
                variant="default"
                size="sm"
                className="h-7 gap-1.5 bg-trust-blue-700 hover:bg-trust-blue-800 text-white"
                onClick={draftOutreach}
                disabled={drafting}
                data-testid="draft-outreach-btn"
              >
                {drafting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Drafting…
                  </>
                ) : (
                  <>
                    <Mail className="w-3.5 h-3.5" /> Draft outreach to this agency
                  </>
                )}
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Row
              label="Agency"
              value={reportingAgency}
              href={reportingSourceUrl}
            />
            {reportingPhone && (
              <Row label="Phone" value={reportingPhone} />
            )}
            {reportingCaseNumber && (
              <Row label="Agency case #" value={reportingCaseNumber} />
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
